import { DiscordActivityPlatformApi, Platform, usePlatform } from '@tofu/platform';
import { ActivateSubscriptionTokenRequest } from '@hotpot-arcade/fanforge-core/types/subscription';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PriceUtils } from '@discord/embedded-app-sdk';
import { useAuth } from '@hotpot-arcade/fanforge-auth';
import { useFanforgeApi } from '@hotpot-arcade/fanforge-api-client';
import { VirtualCurrencyCard } from '../components/virtual-currency-card';
import { BackArrow } from '../components/icons/back-arrow';
import { useAnalytics } from '@hotpot-arcade/analytics-client';
import { Flags } from '@hotpot-arcade/fanforge-core';
import { z } from 'zod';
import { useCreator } from '@hotpot-arcade/fanforge-meta-client';

type SKU = {
  type: 1 | 4 | 2 | 3 | 5 | -1;
  id: string;
  name: string;
  flags: number;
  application_id: string;
  price: {
    amount: number;
    currency: string;
  };
  release_date: string | null;
};

type Entitlement = {
  type: 1 | 4 | 2 | 3 | 5 | 6 | 7 | -1;
  id: string;
  application_id: string;
  user_id: string;
  sku_id: string;
  gift_code_flags: number;
  parent_id?: string | null | undefined;
  gifter_user_id?: string | null | undefined;
  branches?: string[] | null | undefined;
  starts_at?: string | null | undefined;
  ends_at?: string | null | undefined;
  consumed?: boolean | null | undefined;
  deleted?: boolean | null | undefined;
  gift_code_batch_id?: string | null | undefined;
};

const discordSkuMapConfigSchema = z.object({
  consumables: z.object({ skuId: z.string(), displayText: z.string().optional() }).array(),
});

type SkuConfig = z.infer<typeof discordSkuMapConfigSchema>;

export function StorePage() {
  const { platform } = usePlatform();
  const { client: apiClient } = useFanforgeApi();
  const jwt = useAuth((s) => (s.state === 'authenticated' ? s.jwt : null));
  const [skus, setSkus] = useState<SKU[]>([]);
  const [config, setConfig] = useState<SkuConfig>({ consumables: [] });
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { getFeatureFlagPayload, track } = useAnalytics();
  const { data: creator } = useCreator();

  const _refreshEntitlements = useCallback(async (): Promise<void> => {
    if (platform.platform === Platform.DiscordActivity) {
      const api = platform as DiscordActivityPlatformApi;
      const response = await api.discordSdk.commands.getEntitlements();
      setEntitlements(response.entitlements);
    }
  }, [platform]);

  useEffect(() => {
    async function refreshSkus(): Promise<void> {
      if (platform.platform === Platform.DiscordActivity) {
        const api = platform as DiscordActivityPlatformApi;
        const response = await api.discordSdk.commands.getSkus();
        setSkus(response.skus);
      }
    }

    function refreshConfig(): void {
      const payload = getFeatureFlagPayload(Flags.discordSkuMap);
      const result = discordSkuMapConfigSchema.safeParse(payload);
      if (result.success) {
        setConfig(result.data);
      } else {
        console.error(`Error loading config data: ${result.error}`);
      }
    }

    refreshSkus();
    refreshConfig();
    _refreshEntitlements();
  }, [_refreshEntitlements, getFeatureFlagPayload, platform]);

  const subscriptionSkus = useMemo(() => skus.filter((s) => s.type === 5), [skus]);

  const tournamentSkuId = import.meta.env.VITE_TOURNAMENT_TICKET_SKU_ID;
  // type 3 = consumable item
  // flag 0 = unpublished
  const virtualCurrencySkus = useMemo(
    () =>
      skus
        .filter((s) => s.type === 3 && s.flags !== 0 && s.id !== tournamentSkuId)
        .sort((a, b) => a.price.amount - b.price.amount),
    [skus, tournamentSkuId]
  );

  const activeSubscriptionSkuIds = useMemo(() => {
    const subscriptionSkuIds = subscriptionSkus.map((s) => s.id);
    return entitlements.filter((e) => subscriptionSkuIds.includes(e.sku_id) && !e.ends_at).map((e) => e.sku_id);
  }, [entitlements, subscriptionSkus]);

  async function _createInactiveToken(skuId: string): Promise<string> {
    const response = await apiClient.discord[':skuId']['subscription-token'].$post({
      param: {
        skuId,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status} creating subscription token.\n${await response.text()}`);
    }

    return (await response.json()).tokenId;
  }

  async function _activateToken(skuId: string, tokenId: string, body: ActivateSubscriptionTokenRequest): Promise<void> {
    const response = await apiClient.discord[':skuId']['subscription-token'][':tokenId'].subscription.$post({
      json: body,
      param: {
        skuId,
        tokenId,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status} activating subscription token.\n${await response.text()}`);
    }
  }

  async function _handleSubscriptionClick(sku: SKU): Promise<void> {
    setLoading(true);

    try {
      if (platform.platform === Platform.DiscordActivity) {
        const api = platform as DiscordActivityPlatformApi;
        const tokenId = await _createInactiveToken(sku.id);

        const createdEntitlements = await api.discordSdk.commands.startPurchase({ sku_id: sku.id });
        if (createdEntitlements === null || createdEntitlements.length === 0) {
          return;
        }

        if (createdEntitlements?.length !== 1) {
          throw new Error('Unexpected response from startPurchase command');
        }
        const entitlement = createdEntitlements[0];
        if (entitlement === undefined) {
          throw new Error('Unexpected response from startPurchase command');
        }

        setEntitlements((prev) => [...prev, entitlement]);
        await _activateToken(sku.id, tokenId, {
          entitlement: {
            id: entitlement.id,
            startedAt: new Date(entitlement.starts_at!),
            endedAt: entitlement.ends_at ? new Date(entitlement.ends_at) : undefined,
            active: true,
          },
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function _handleVirtualCurrencyPurchase(sku: SKU): Promise<void> {
    setLoading(true);
    try {
      if (platform.platform === Platform.DiscordActivity) {
        track({
          name: 'coin_shop_purchase_button_clicked',
          props: {
            guild_id: creator?.discordGuildId,
            sku: {
              id: sku.id,
              name: sku.name,
            },
          },
        });

        const api = platform as DiscordActivityPlatformApi;
        await api.discordSdk.commands.startPurchase({ sku_id: sku.id });

        track({
          name: 'coin_shop_purchase_completed',
          props: {
            guild_id: creator?.discordGuildId,
            sku: {
              id: sku.id,
              name: sku.name,
            },
          },
        });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="relative flex flex-row gap-5 p-3 border-b-[0.5px] border-b-[#2D2D31]">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="bg-customgreen text-customdarkgray rounded-3xl py-2 px-3 font-bold transform transition duration-300 ease-out hover:scale-[1.03] flex flex-row"
        >
          <div className="pt-1.5 pr-1">
            <BackArrow />
          </div>
          <div>Back</div>
        </button>
        <img src="images/fanforge-logo.svg" alt="Fanforge Logo" />
        <svg width="1" height="25" viewBox="0 0 1 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0.25 0V24.5" stroke="#2D2D31" stroke-width="0.5" />
        </svg>
        <p className="text-white text-2xl font-semibold">Shop</p>
      </div>
      {virtualCurrencySkus.length > 0 && ( // virtual currency
        <div className="p-3">
          <h3 className="text-customgray text-xl mb-3 mt-3">Currency</h3>
          <ul className="flex flex-row flex-wrap gap-3">
            {virtualCurrencySkus
              .filter((sku) => config.consumables.find((c) => c.skuId === sku.id) !== undefined)
              .map((sku) => (
                <li key={sku.id}>
                  <VirtualCurrencyCard
                    title={sku.name}
                    description={config.consumables.find((c) => c.skuId === sku.id)?.displayText ?? ''}
                    formattedPrice={PriceUtils.formatPrice(sku.price)}
                    onPurchaseButtonClick={async () => await _handleVirtualCurrencyPurchase(sku)}
                  />
                </li>
              ))}
          </ul>
        </div>
      )}
    </>
  );
}
